/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useState, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Box,
    useBreakpointValue,
    Flex
} from '@chakra-ui/react'
import SearchSuggestions from './partials/search-suggestions'
import {SearchIcon} from '../icons'
import {
    capitalize,
    boldString,
    getSessionJSONItem,
    setSessionJSONItem,
    noop
} from '../../utils/utils'
import useHybridURL from '../../hooks/use-hybrid-url'
import debounce from 'lodash/debounce'
import {
    HYBRID_PDP,
    HYBRID_PLP,
    HYBRID_SEARCH,
    RECENT_SEARCH_KEY,
    RECENT_SEARCH_LIMIT,
    RECENT_SEARCH_MIN_LENGTH
} from '../../constants'
import {productUrlBuilder, searchUrlBuilder, categoryUrlBuilder} from '../../utils/url'
import {FormattedMessage} from 'react-intl'
import {trackEvent} from '../../tracking/analytics'
import useEffectEvent from '../../hooks/use-effect-event'
import {useSearchSuggestions} from '@salesforce/commerce-sdk-react'

const formatSuggestions = (locale, searchSuggestions, input) => {
    return {
        categorySuggestions: searchSuggestions?.categorySuggestions?.categories?.map(
            (suggestion) => {
                return {
                    type: 'category',
                    id: suggestion.id,
                    link: categoryUrlBuilder({id: suggestion.id}),
                    hybrid: HYBRID_PLP,
                    name: boldString(suggestion.name, capitalize(input))
                }
            }
        ),
        productSuggestions: searchSuggestions?.productSuggestions?.products
            ?.filter((product) => product.productName)
            ?.map((product) => {
                return {
                    type: 'product',
                    productId: product.productId,
                    name: boldString(product.productName, capitalize(input)),
                    link: productUrlBuilder(product, locale),
                    hybrid: HYBRID_PDP,
                    image: product.c_image
                }
            }),
        phraseSuggestions: searchSuggestions?.productSuggestions?.suggestedPhrases
            ?.map((phrase) => {
                return {
                    type: 'phrase',
                    name: boldString(phrase.phrase, capitalize(input)),
                    link: searchUrlBuilder(phrase.phrase),
                    hybrid: HYBRID_SEARCH
                }
            })
            .slice(0, 2)
    }
}

/**
 * The SearchInput component is a stylized
 * text input made specifically for use in
 * the application header.
 * @param  {object} props
 * @param  {object} ref reference to the input element
 * @return  {React.ReactElement} - SearchInput component
 */
const Search = ({
    hideSearch = noop,
    leftIcon,
    withSearchButton = false,
    popoverOverrides,
    searchBarPosition = 'Top',
    searchQuery: initialSearchQuery,
    relative = false,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '')
    const navigateHybrid = useHybridURL()
    const searchSuggestion = useSearchSuggestions(
        {
            parameters: {
                q: searchQuery,
                limit: 3
            }
        },
        {
            enabled: searchQuery?.length >= RECENT_SEARCH_MIN_LENGTH
        }
    )
    const searchInputRef = useRef()
    const recentSearches = getSessionJSONItem(RECENT_SEARCH_KEY)
    const isDesktop = useBreakpointValue({lg: true})
    const {locale} = useIntl()
    const searchSuggestions = useMemo(
        () => formatSuggestions(locale, searchSuggestion.data, searchInputRef.current?.value || ''),
        [locale, searchSuggestion.data]
    )

    // check if popover should open if we have suggestions
    const onSearchSuggestionsChange = useEffectEvent(() => shouldOpenPopover())
    useEffect(() => {
        onSearchSuggestionsChange(searchSuggestions)
    }, [searchSuggestions, onSearchSuggestionsChange])

    // search input default value
    useEffect(() => {
        if (initialSearchQuery != null) {
            setSearchQuery(initialSearchQuery)
            searchInputRef.current.value = initialSearchQuery
        }
    }, [initialSearchQuery])
    // search input tracking
    const onSearchQueryChange = useEffectEvent((searchQuery) => {
        trackEvent({
            event: 'search',
            search_bar_position: searchBarPosition,
            search_query: searchQuery
        })
    })
    useEffect(() => {
        if (searchQuery) {
            onSearchQueryChange(searchQuery)
        }
    }, [searchQuery, onSearchQueryChange])

    const searchSuggestionsAvailable =
        searchSuggestions &&
        (searchSuggestions?.categorySuggestions?.length ||
            searchSuggestions?.phraseSuggestions?.length ||
            searchSuggestions?.productSuggestions?.length)

    const saveRecentSearch = (searchText) => {
        // Get recent searches or an empty array if undefined.
        let searches = getSessionJSONItem(RECENT_SEARCH_KEY) || []

        // Check if term is already in the saved searches
        searches = searches.filter((savedSearchTerm) => {
            return searchText.toLowerCase() !== savedSearchTerm.toLowerCase()
        })

        // Create a new array consisting of the search text and up to 4 other resent searches.
        // I'm assuming the order is newest to oldest.
        searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT)

        // Replace the save resent search with the updated value.
        setSessionJSONItem(RECENT_SEARCH_KEY, searches)
    }

    // search suggestions are disabled as they are not yet adjusted for STOKKE project
    const debouncedSearch = debounce((input) => {
        debouncedSearch.cancel()
        setSearchQuery(input)
    }, 300)

    const onSearchChange = async (e) => {
        const input = e.target.value
        if (input.length >= RECENT_SEARCH_MIN_LENGTH) {
            debouncedSearch(input)
        }
    }

    const clearInput = () => {
        searchInputRef.current.blur()
        setIsOpen(false)
    }

    const onSubmitSearch = (e) => {
        e.preventDefault()
        // Avoid blank spaces to be searched
        let searchText = searchInputRef.current.value.trim()
        // Avoid empty string searches
        if (searchText.length < 1) {
            return
        }

        saveRecentSearch(searchText)
        clearInput()
        navigateHybrid(HYBRID_SEARCH, searchUrlBuilder(searchText))
        hideSearch()
    }

    const closeAndNavigate = (page, link, trackingData) => {
        hideSearch()
        clearInput()
        setIsOpen(false)

        trackEvent({
            event: 'search',
            search_query: searchInputRef.current?.value,
            ...trackingData
        })

        if (page && link) {
            navigateHybrid(page, link)
        }
    }

    const shouldOpenPopover = () => {
        // As per design we only want to show the popover if the input is focused and we have recent searches saved
        // or we have search suggestions available and have inputed some text (empty text in this scenario should show recent searches)
        if (
            (document.activeElement.id === 'search-input' && recentSearches?.length > 0) ||
            (searchSuggestionsAvailable && searchInputRef.current.value.length > 0)
        ) {
            setIsOpen(true)
        } else {
            setIsOpen(false)
        }
    }

    const onSearchInputChange = (e) => {
        onSearchChange(e)
        shouldOpenPopover()
    }

    const deleteInput = () => {
        searchInputRef.current.value = ''
        clearInput()
        setIsOpen(false)
        searchInputRef.current.focus()
    }

    const PopoverNode = relative ? Box : PopoverContent

    return (
        <Box width="full">
            <Popover
                isOpen={isOpen}
                isLazy
                initialFocusRef={searchInputRef}
                variant="searchSuggestions"
                placement="bottom-start"
                styles={popoverOverrides}
            >
                <form onSubmit={onSubmitSearch} data-testid="search-form">
                    <Flex
                        justifyItems="center"
                        gap={2}
                        flexWrap={{base: 'wrap', md: 'nowrap'}}
                        justifyContent="center"
                    >
                        <PopoverTrigger>
                            <InputGroup maxWidth="728px">
                                {leftIcon && (
                                    <InputLeftElement
                                        width="auto"
                                        paddingLeft={2}
                                        pointerEvents="none"
                                    >
                                        <SearchIcon />
                                    </InputLeftElement>
                                )}
                                <Input
                                    autoComplete="off"
                                    id="search-input"
                                    onChange={(e) => onSearchInputChange(e)}
                                    onFocus={() => shouldOpenPopover()}
                                    onBlur={() => setIsOpen(false)}
                                    type="search"
                                    ref={searchInputRef}
                                    // Styles to prevent autofocus search input on iOS
                                    // https://www.youtube.com/watch?v=uYZl86TMQ4M
                                    sx={{
                                        '&::autofill': {
                                            display: 'none'
                                        },
                                        '&::-webkit-autofill': {
                                            display: 'none'
                                        },
                                        '&::-webkit-autofill-display': {
                                            display: 'none'
                                        }
                                    }}
                                    {...props}
                                />
                                {searchInputRef.current?.value && (
                                    <InputRightElement width="auto" paddingRight={2}>
                                        <Button variant="link" onClick={deleteInput} size="sm">
                                            {isDesktop ? (
                                                <FormattedMessage
                                                    defaultMessage="Delete input"
                                                    id="product_list.search.delete_input"
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    defaultMessage="Delete"
                                                    id="global.delete"
                                                />
                                            )}
                                        </Button>
                                    </InputRightElement>
                                )}
                            </InputGroup>
                        </PopoverTrigger>
                        {withSearchButton && (
                            <Button
                                variant="solid"
                                type="submit"
                                width={{base: 'full', md: '400px'}}
                            >
                                <FormattedMessage
                                    defaultMessage="Search"
                                    id="empty_cart.button.search"
                                />
                            </Button>
                        )}
                    </Flex>
                </form>

                {shouldOpenPopover && (
                    <PopoverNode
                        paddingTop={relative && 6}
                        data-testid="sf-suggestion-popover"
                        maxWidth="100vw"
                        width="100%"
                    >
                        <SearchSuggestions
                            closeAndNavigate={closeAndNavigate}
                            recentSearches={recentSearches}
                            searchSuggestions={searchSuggestions}
                            onSubmitSearch={onSubmitSearch}
                            searchBarPosition={searchBarPosition}
                        />
                    </PopoverNode>
                )}
            </Popover>
        </Box>
    )
}

Search.propTypes = {
    hideSearch: PropTypes.func,
    leftIcon: PropTypes.node,
    withSearchButton: PropTypes.bool,
    popoverOverrides: PropTypes.object,
    searchBarPosition: PropTypes.string,
    searchQuery: PropTypes.string,
    relative: PropTypes.bool
}

Search.displayName = 'SearchInput'

export default Search
